import React from "react";
import AddIcon from "@mui/icons-material/Add";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryIcon from "@mui/icons-material/History";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import {
  canPublishScenario,
  formatPublicationEffectiveDate,
  getPublicationDisplayName,
  getPublicationStatus,
  getScenarioDisplayDescription,
  getScenarioDisplayName,
} from "./laborModelPublicationStatus";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 520,
  maxWidth: "calc(100vw - 32px)",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  padding: "24px",
};

const fieldSx = {
  "& .MuiInputLabel-root": {
    color: "#64748b",
    fontSize: "0.875rem",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#6d28d9",
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    fontSize: "0.875rem",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#cbd5e1",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#94a3b8",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6d28d9",
      borderWidth: "1px",
    },
  },
};

const secondaryButtonClass =
  "inline-flex h-9 items-center justify-center rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

const primaryButtonClass =
  "inline-flex h-9 items-center justify-center rounded-lg bg-violet-700 px-4 text-sm font-semibold text-white transition-colors hover:bg-violet-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500";

export function PublishedModelCard({
  publication,
  selected,
  activeUuid,
  onSelect,
}) {
  const status = getPublicationStatus(publication, activeUuid);
  const effectiveDate = formatPublicationEffectiveDate(
    publication.effective_date
  );
  const statusClasses = {
    live: "bg-emerald-100 text-emerald-800",
    scheduled: "bg-amber-100 text-amber-800",
    previous: "bg-slate-100 text-slate-600",
    muted: "bg-slate-100 text-slate-500",
  };

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(publication)}
      className={`h-[108px] w-[230px] shrink-0 rounded-xl border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2 ${
        selected
          ? "border-violet-600 bg-violet-50 shadow-[inset_0_0_0_1px_#7c3aed]"
          : "border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
      }`}
    >
      <span className="block truncate text-sm font-semibold text-slate-900">
        {getPublicationDisplayName(publication)}
      </span>
      <span className="mt-2 flex min-w-0 flex-col items-start gap-1">
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
            statusClasses[status.tone]
          }`}
        >
          {["live", "scheduled"].includes(status.tone)
            ? status.label
            : status.shortLabel}
        </span>
        {effectiveDate && !["live", "scheduled"].includes(status.tone) ? (
          <span className="block w-full whitespace-nowrap text-xs text-slate-500">
            Effective {effectiveDate}
          </span>
        ) : null}
      </span>
    </button>
  );
}

function SandboxScenarioCard({
  scenario,
  selected,
  onSelect,
  onOpenMenu,
}) {
  const changeCount = Number.isFinite(Number(scenario.change_count))
    ? Number(scenario.change_count)
    : 0;

  return (
    <div
      className={`relative h-[108px] w-[260px] shrink-0 rounded-xl border transition-colors ${
        selected
          ? "border-violet-600 bg-violet-50 shadow-[inset_0_0_0_1px_#7c3aed]"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <button
        type="button"
        aria-pressed={selected}
        onClick={() => onSelect(scenario.uuid)}
        className="h-full w-full rounded-xl p-3 pr-11 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700">
            <ScienceOutlinedIcon aria-hidden="true" sx={{ fontSize: 17 }} />
          </span>
          <span className="truncate text-sm font-semibold text-slate-900">
            {getScenarioDisplayName(scenario)}
          </span>
        </span>
        <span
          className={`mt-2 block truncate text-xs font-medium ${
            changeCount > 0 ? "text-amber-700" : "text-slate-500"
          }`}
        >
          {changeCount} change{changeCount === 1 ? "" : "s"}
        </span>
      </button>
      <button
        type="button"
        aria-label={`Actions for ${getScenarioDisplayName(scenario)}`}
        aria-haspopup="menu"
        onClick={(event) => onOpenMenu(event, scenario)}
        className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-white hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
      >
        <MoreVertIcon aria-hidden="true" sx={{ fontSize: 20 }} />
      </button>
    </div>
  );
}

function LaborModelScenarioHeader({
  scenarios = [],
  selectedScenario,
  selectedScenarioUuid,
  onSelect,
  onCreate,
  onRename,
  onArchive,
  onCompare,
  onHistory,
  onPublish,
  viewMode = "saved",
  selectedPublication,
  activePublicationUuid,
  activePublication,
  publications = [],
  onViewPublication,
}) {
  const [createOpen, setCreateOpen] = React.useState(false);
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [renameName, setRenameName] = React.useState("");
  const [renameDescription, setRenameDescription] = React.useState("");
  const [createSourceScenario, setCreateSourceScenario] = React.useState(null);
  const [editingScenario, setEditingScenario] = React.useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [menuScenario, setMenuScenario] = React.useState(null);

  const scheduledPublications = React.useMemo(
    () =>
      publications
        .filter(
          (publication) =>
            publication.uuid !== activePublicationUuid &&
            getPublicationStatus(publication, activePublicationUuid).tone ===
              "scheduled"
        )
        .sort((left, right) =>
          String(left.effective_date).localeCompare(String(right.effective_date))
        ),
    [activePublicationUuid, publications]
  );
  const selectedPublicationSummary =
    publications.find(
      (publication) => publication.uuid === selectedPublication?.uuid
    ) || selectedPublication;
  const selectedHistoricalPublication =
    viewMode === "published" &&
    selectedPublicationSummary &&
    selectedPublicationSummary.uuid !== activePublicationUuid &&
    !scheduledPublications.some(
      (publication) => publication.uuid === selectedPublicationSummary.uuid
    )
      ? selectedPublicationSummary
      : null;
  const visiblePublications = [
    ...(activePublication ? [activePublication] : []),
    ...scheduledPublications,
    ...(selectedHistoricalPublication ? [selectedHistoricalPublication] : []),
  ];

  const selectScenario = (scenarioUuid) => {
    onSelect(scenarioUuid);
  };

  const openCreate = (sourceScenario = null) => {
    setCreateSourceScenario(sourceScenario);
    setName("");
    setDescription("");
    setCreateOpen(true);
  };

  const saveCreate = async () => {
    setSaving(true);
    try {
      await onCreate({
        name: name.trim(),
        description: description.trim(),
        source_type: createSourceScenario ? "scenario" : "active",
        source_uuid: createSourceScenario?.uuid || null,
      });
      setCreateOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const submitCreate = (event) => {
    event.preventDefault();
    if (!saving && name.trim()) saveCreate();
  };

  const openRename = (scenario) => {
    setRenameName(getScenarioDisplayName(scenario));
    setRenameDescription(getScenarioDisplayDescription(scenario));
    setEditingScenario(scenario);
    setRenameOpen(true);
  };

  const saveRename = async () => {
    if (!editingScenario || !renameName.trim()) return;
    setSaving(true);
    try {
      await onRename(
        {
          name: renameName.trim(),
          description: renameDescription.trim(),
        },
        editingScenario
      );
      setRenameOpen(false);
      setEditingScenario(null);
    } finally {
      setSaving(false);
    }
  };

  const submitRename = (event) => {
    event.preventDefault();
    if (!saving) saveRename();
  };

  const deleteScenario = async (scenario) => {
    if (!scenario) return;
    const confirmed = window.confirm(
      `Delete ${getScenarioDisplayName(scenario)}?\n\nPublished models and history will not be changed. If this is the last sandbox, you will return to the current published model.`
    );
    if (confirmed) {
      await onArchive(scenario);
    }
  };

  const openScenarioMenu = (event, scenario) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuScenario(scenario);
    if (selectedScenarioUuid !== scenario.uuid) {
      selectScenario(scenario.uuid);
    }
  };

  const closeScenarioMenu = () => {
    setMenuAnchorEl(null);
  };

  const runMenuAction = (action) => {
    const scenario = menuScenario;
    closeScenarioMenu();
    if (!scenario) return;
    if (action === "duplicate") openCreate(scenario);
    if (action === "rename") openRename(scenario);
    if (action === "compare") onCompare(scenario);
    if (action === "delete") deleteScenario(scenario);
  };

  const selectedScenarioSummary =
    scenarios.find((scenario) => scenario.uuid === selectedScenarioUuid) ||
    selectedScenario;
  const canPublish = canPublishScenario(viewMode, selectedScenarioSummary);

  return (
    <>
      <section className="border-b border-slate-200 bg-white">
        <div className="px-6 py-4 sm:px-4">
          <div className="mb-4 flex items-center justify-between gap-4 sm:items-start">
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                Labor models
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Choose a published model or sandbox scenario to view below.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2 sm:flex-col-reverse sm:items-stretch">
              <button
                type="button"
                className={secondaryButtonClass}
                onClick={onHistory}
              >
                <HistoryIcon className="mr-1.5" fontSize="small" />
                History
              </button>
              <button
                type="button"
                className={primaryButtonClass}
                onClick={onPublish}
                disabled={!canPublish}
                title={
                  viewMode === "published"
                    ? "Select a sandbox scenario to publish changes"
                    : "Publish this sandbox scenario"
                }
              >
                <PublishOutlinedIcon className="mr-1.5" fontSize="small" />
                Publish to production
              </button>
            </div>
          </div>

          <div className="flex items-start gap-5 sm:flex-col">
            <div className="shrink-0 py-1">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-emerald-700">
                Published models
              </p>
              <div className="flex gap-3">
                {visiblePublications.length ? (
                  visiblePublications.map((publication) => (
                    <PublishedModelCard
                      key={publication.uuid}
                      publication={publication}
                      activeUuid={activePublicationUuid}
                      selected={
                        viewMode === "published" &&
                        selectedPublication?.uuid === publication.uuid
                      }
                      onSelect={onViewPublication}
                    />
                  ))
                ) : (
                  <div className="flex h-[108px] w-[230px] items-center rounded-xl border border-dashed border-slate-300 px-4 text-sm text-slate-500">
                    No published model yet
                  </div>
                )}
              </div>
            </div>

            <div
              className="mt-7 h-[108px] w-px shrink-0 bg-slate-200 sm:mt-0 sm:h-px sm:w-full"
              aria-hidden="true"
            />

            <div className="min-w-0 flex-1 pt-1 sm:w-full">
              <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Sandbox scenarios
              </p>
              <div className="overflow-x-auto pb-2">
                <div className="min-w-max px-1">
                <div className="flex gap-3">
                  {scenarios.map((scenario) => (
                    <SandboxScenarioCard
                      key={scenario.uuid}
                      scenario={scenario}
                      selected={
                        viewMode === "saved" &&
                        selectedScenarioUuid === scenario.uuid
                      }
                      onSelect={selectScenario}
                      onOpenMenu={openScenarioMenu}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => openCreate(null)}
                    className="flex h-[108px] w-[180px] shrink-0 flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 text-sm font-semibold text-slate-700 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
                  >
                    <AddIcon aria-hidden="true" fontSize="small" />
                    <span className="mt-1">New sandbox scenario</span>
                  </button>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeScenarioMenu}
        MenuListProps={{
          "aria-label": menuScenario
            ? `Actions for ${menuScenario.name}`
            : "Sandbox scenario actions",
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 210,
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.14)",
            },
          },
        }}
      >
        <MenuItem onClick={() => runMenuAction("duplicate")}>
          <ListItemIcon><ContentCopyOutlinedIcon fontSize="small" /></ListItemIcon>
          Duplicate
        </MenuItem>
        <MenuItem onClick={() => runMenuAction("rename")}>
          <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
          Rename
        </MenuItem>
        <MenuItem onClick={() => runMenuAction("compare")}>
          <ListItemIcon><CompareArrowsOutlinedIcon fontSize="small" /></ListItemIcon>
          Compare with published
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => runMenuAction("delete")}
          sx={{
            color: "#be123c",
          }}
        >
          <span className="flex w-full items-center">
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" sx={{ color: "#be123c" }} />
            </ListItemIcon>
            Delete sandbox scenario
          </span>
        </MenuItem>
      </Menu>

      <Modal
        open={createOpen}
        onClose={saving ? undefined : () => setCreateOpen(false)}
        aria-labelledby="create-sandbox-scenario-title"
        aria-describedby="create-sandbox-scenario-description"
      >
        <Box component="form" onSubmit={submitCreate} sx={modalStyle}>
          <h2 id="create-sandbox-scenario-title" className="text-xl font-semibold text-slate-900">
            {createSourceScenario
              ? "Duplicate sandbox scenario"
              : "Create a sandbox scenario"}
          </h2>
          <p id="create-sandbox-scenario-description" className="mt-1 text-sm text-slate-500">
            This makes a copy of{" "}
            <span className="font-medium text-slate-700">
              {createSourceScenario?.name || "the current published model"}
            </span>
            . Nothing is published until you choose Publish to production.
          </p>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Sandbox scenario name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            sx={{ marginTop: 3, ...fieldSx }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="What are you testing? (optional)"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            sx={{ marginTop: 2, ...fieldSx }}
          />
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              className={secondaryButtonClass}
              onClick={() => setCreateOpen(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={primaryButtonClass}
              disabled={saving || !name.trim()}
            >
              {saving
                ? "Creating…"
                : createSourceScenario
                ? "Duplicate sandbox scenario"
                : "Create sandbox scenario"}
            </button>
          </div>
        </Box>
      </Modal>

      <Modal
        open={renameOpen}
        onClose={
          saving
            ? undefined
            : () => {
                setRenameOpen(false);
                setEditingScenario(null);
              }
        }
        aria-labelledby="sandbox-scenario-details-title"
        aria-describedby="sandbox-scenario-details-description"
      >
        <Box component="form" onSubmit={submitRename} sx={modalStyle}>
          <h2 id="sandbox-scenario-details-title" className="text-xl font-semibold text-slate-900">
            Rename sandbox scenario
          </h2>
          <p id="sandbox-scenario-details-description" className="mt-1 text-sm text-slate-500">
            Update the name or note for this sandbox scenario.
          </p>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Sandbox scenario name"
            value={renameName}
            onChange={(event) => setRenameName(event.target.value)}
            sx={{ marginTop: 3, ...fieldSx }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Description"
            value={renameDescription}
            onChange={(event) => setRenameDescription(event.target.value)}
            sx={{ marginTop: 2, ...fieldSx }}
          />
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              className={secondaryButtonClass}
              onClick={() => {
                setRenameOpen(false);
                setEditingScenario(null);
              }}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={primaryButtonClass}
              disabled={saving || !renameName.trim()}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </Box>
      </Modal>
    </>
  );
}

export default LaborModelScenarioHeader;
